import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import './BillingManagement.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface BillingRecord {
  id: string;
  eventId: string;
  eventName: string;
  eventDate: string;
  organizerId: string;
  organizerName: string;
  organizerEmail: string;
  organizerPhone: string;
  invoiceAddress: {
    street: string;
    postalCode: string;
    city: string;
    country: string;
  };
  invoiceReference: string;
  orgNumber?: string;
  pricePerEvent: number;
  netDays: number;
  status: 'Opprettet' | 'Fakturert' | 'Betalt';
  notes: string;
  createdAt: string;
  updatedAt: string;
}

const BillingManagement: React.FC = () => {
  const { accessToken } = useAuth();
  const [billingRecords, setBillingRecords] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'Opprettet' | 'Fakturert' | 'Betalt'>('all');
  const [sortColumn, setSortColumn] = useState<keyof BillingRecord>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showEditModal, setShowEditModal] = useState<BillingRecord | null>(null);
  const [editStatus, setEditStatus] = useState<'Opprettet' | 'Fakturert' | 'Betalt'>('Opprettet');
  const [editNotes, setEditNotes] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [organizerUserName, setOrganizerUserName] = useState<string>('');
  const [userNamesMap, setUserNamesMap] = useState<{ [userId: string]: string }>({});

  useEffect(() => {
    fetchBillingRecords();
    fetchUsers();
  }, [accessToken]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const usersMap: { [userId: string]: string } = {};
      response.data.users.forEach((user: any) => {
        usersMap[user.id] = user.name;
      });
      setUserNamesMap(usersMap);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchBillingRecords = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/billing`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setBillingRecords(response.data.billingRecords || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke hente faktureringsdata');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = async (record: BillingRecord) => {
    setShowEditModal(record);
    setEditStatus(record.status);
    setEditNotes(record.notes);
    
    // Get the organizer user's name from the map
    setOrganizerUserName(userNamesMap[record.organizerId] || 'Ukjent');
  };

  const handleSaveEdit = async () => {
    if (!showEditModal) return;

    try {
      setProcessingId(showEditModal.id);
      await axios.patch(
        `${API_URL}/billing/${showEditModal.id}`,
        { status: editStatus, notes: editNotes },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      await fetchBillingRecords();
      setShowEditModal(null);
      setEditStatus('Opprettet');
      setEditNotes('');
      setSuccessMessage('Faktureringsdata oppdatert');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke oppdatere faktureringsdata');
      setTimeout(() => setError(''), 5000);
    } finally {
      setProcessingId(null);
    }
  };

  const handleSort = (column: keyof BillingRecord) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'Opprettet': return 'Opprettet';
      case 'Fakturert': return 'Fakturert';
      case 'Betalt': return 'Betalt';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Opprettet': return 'status-created';
      case 'Fakturert': return 'status-invoiced';
      case 'Betalt': return 'status-paid';
      default: return '';
    }
  };

  // Filter and sort records
  const filteredRecords = billingRecords
    .filter((record) => {
      const matchesSearch =
        record.eventName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.organizerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.organizerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.invoiceReference?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      const multiplier = sortDirection === 'asc' ? 1 : -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * multiplier;
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * multiplier;
      }
      return 0;
    });

  const statusCounts = {
    all: billingRecords.length,
    Opprettet: billingRecords.filter((r) => r.status === 'Opprettet').length,
    Fakturert: billingRecords.filter((r) => r.status === 'Fakturert').length,
    Betalt: billingRecords.filter((r) => r.status === 'Betalt').length,
  };

  if (loading) {
    return <div className="loading-state">Laster faktureringsdata...</div>;
  }

  return (
    <div className="billing-management">
      <div className="billing-header">
        <h2>Fakturering</h2>
        <p className="billing-subtitle">
          Administrer og hold oversikt over fakturainformasjon for Sykkelfest-arrangementer. Fakturering må skje i eksternt system.
        </p>
      </div>

      {successMessage && (
        <div className="message-banner success">{successMessage}</div>
      )}
      {error && <div className="message-banner error">{error}</div>}

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Søk etter arrangement, arrangør, e-post eller referanse..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="status-filter"
        >
          <option value="all">Alle statuser</option>
          <option value="Opprettet">Opprettet</option>
          <option value="Fakturert">Fakturert</option>
          <option value="Betalt">Betalt</option>
        </select>
      </div>

      <div className="billing-table-container">
        <table className="billing-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('eventDate')} className="sortable">
                Dato {sortColumn === 'eventDate' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('eventName')} className="sortable">
                Arrangement {sortColumn === 'eventName' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('organizerName')} className="sortable">
                Arrangør {sortColumn === 'organizerName' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('pricePerEvent')} className="sortable">
                Beløp {sortColumn === 'pricePerEvent' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Netto dager</th>
              <th onClick={() => handleSort('status')} className="sortable">
                Status {sortColumn === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('createdAt')} className="sortable">
                Opprettet {sortColumn === 'createdAt' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-state">
                  Ingen faktureringsdata funnet
                </td>
              </tr>
            ) : (
              filteredRecords.map((record) => (
                <tr key={record.id}>
                  <td>
                    {new Date(record.eventDate).toLocaleDateString('nb-NO', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </td>
                  <td>{record.eventName}</td>
                  <td>
                    <div>{record.organizerName}</div>
                    <small className="text-muted">{userNamesMap[record.organizerId] || 'Ukjent'}</small>
                  </td>
                  <td className="text-right">{record.pricePerEvent.toLocaleString('nb-NO')} kr</td>
                  <td>{record.netDays}</td>
                  <td>
                    <span className={`status-badge ${getStatusColor(record.status)}`}>
                      {getStatusLabel(record.status)}
                    </span>
                  </td>
                  <td>
                    {new Date(record.createdAt).toLocaleDateString('nb-NO', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </td>
                  <td>
                    <button
                      onClick={() => handleOpenEdit(record)}
                      className="btn btn-small btn-secondary"
                    >
                      Detaljer
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Rediger/vis faktureringsdata</h3>

            <div className="modal-info">
              <p><strong>Arrangement:</strong> {showEditModal.eventName}</p>
              <p><strong>Arrangør:</strong> {showEditModal.organizerName}</p>
              <p><strong>Att:</strong> {organizerUserName}</p>
              <p>
                <strong>Fakturaadresse:</strong><br />
                {showEditModal.invoiceAddress.street}<br />
                {showEditModal.invoiceAddress.postalCode} {showEditModal.invoiceAddress.city}
              </p>
              <p><strong>Epost:</strong> {showEditModal.organizerEmail}</p>
              <p><strong>Telefon:</strong> {showEditModal.organizerPhone}</p>
              <p><strong>Referanse:</strong> {showEditModal.invoiceReference || 'Ingen'}</p>
              {showEditModal.orgNumber && (
                <p><strong>Org.nr:</strong> {showEditModal.orgNumber}</p>
              )}
              <p><strong>Beløp:</strong> {showEditModal.pricePerEvent.toLocaleString('nb-NO')} kr</p>
              <p><strong>Netto dager:</strong> {showEditModal.netDays}</p>
            </div>

            <div className="form-group">
              <label htmlFor="editStatus">Status</label>
              <select
                id="editStatus"
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value as any)}
                className="form-input"
              >
                <option value="Opprettet">Opprettet</option>
                <option value="Fakturert">Fakturert</option>
                <option value="Betalt">Betalt</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="editNotes">Notater</label>
              <textarea
                id="editNotes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                className="form-input"
                rows={4}
                placeholder="Legg til notater om fakturaen... fakturanr, fakturaprogram, forfallsdato etc."
              />
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowEditModal(null)} className="btn btn-secondary">
                Avbryt
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={processingId === showEditModal.id}
                className="btn btn-primary"
              >
                {processingId === showEditModal.id ? 'Lagrer...' : 'Lagre endringer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingManagement;
